import { useState, useRef } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { adminApi, type UserSummary } from "@/lib/admin-api";
import { centsToDisplay, formatDate } from "./-utils";
import { UserDetail } from "./-components/UserDetail";

interface UsersSearch {
  q?: string;
  page?: number;
}

const PAGE_SIZE = 50;

export const Route = createFileRoute("/admin/users")({
  validateSearch: (s: Record<string, unknown>): UsersSearch => ({
    q: typeof s.q === "string" && s.q ? s.q : undefined,
    page: typeof s.page === "number" && s.page > 0 ? s.page : undefined,
  }),
  component: AdminUsersPage,
});

function AdminUsersPage() {
  const navigate = useNavigate({ from: "/admin/users" });
  const search = Route.useSearch();
  const q = search.q ?? "";
  const page = search.page ?? 0;
  const offset = page * PAGE_SIZE;

  const [localQ, setLocalQ] = useState(q);
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  function handleSearchChange(value: string) {
    setLocalQ(value);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      void navigate({
        search: { q: value || undefined, page: undefined },
        replace: true,
      });
    }, 300);
  }

  const { data, isLoading, isError } = useQuery({
    queryKey: ["admin", "users", q, offset],
    queryFn: () => adminApi.listUsers(q, PAGE_SIZE, offset),
  });

  const users = data?.users ?? [];
  const total = data?.total ?? 0;
  const hasPrev = page > 0;
  const hasNext = offset + PAGE_SIZE < total;

  function goToPage(next: number) {
    void navigate({
      search: { q: q || undefined, page: next > 0 ? next : undefined },
    });
  }

  return (
    <div className="admin-page" data-testid="admin-users">
      <div className="admin-page__header">
        <h1 className="admin-page__title">USERS</h1>
        <div className="admin-users-search">
          <input
            type="text"
            className="admin-input admin-input--search"
            placeholder="Search by email…"
            value={localQ}
            onChange={(e) => handleSearchChange(e.target.value)}
            aria-label="Search users"
            data-testid="users-search"
          />
        </div>
      </div>

      {isLoading && (
        <div className="admin-loading" data-testid="users-loading">
          LOADING…
        </div>
      )}

      {isError && (
        <div className="admin-error" data-testid="users-error">
          Failed to load users.
        </div>
      )}

      {!isLoading && !isError && (
        <>
          <div className="admin-table-wrap">
            <table className="admin-table admin-table--interactive" data-testid="users-table">
              <thead>
                <tr>
                  <th>Email</th>
                  <th>Role</th>
                  <th>Balance</th>
                  <th>Automatons</th>
                  <th>Banned</th>
                  <th>Created</th>
                </tr>
              </thead>
              <tbody>
                {users.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="admin-table__empty">
                      No users found.
                    </td>
                  </tr>
                ) : (
                  users.map((u: UserSummary) => (
                    <tr
                      key={u.id}
                      className="admin-table__row--clickable"
                      onClick={() => setSelectedUserId(u.id)}
                      data-testid={`user-row-${u.id}`}
                      tabIndex={0}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") setSelectedUserId(u.id);
                      }}
                    >
                      <td data-testid="user-email">{u.email}</td>
                      <td>
                        <span
                          className={`admin-badge admin-badge--${u.role === "admin" ? "admin" : "user"}`}
                        >
                          {u.role}
                        </span>
                      </td>
                      <td>{centsToDisplay(u.balance_c)}</td>
                      <td>{u.automaton_count}</td>
                      <td>{u.deleted_at ? "Y" : "N"}</td>
                      <td>{formatDate(u.created_at)}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          <div className="admin-pagination" data-testid="pagination">
            <button
              className="access-btn access-btn--ghost access-btn--sm"
              disabled={!hasPrev}
              onClick={() => goToPage(page - 1)}
              data-testid="pagination-prev"
            >
              ← PREV
            </button>
            <span className="admin-pagination__info">
              {total === 0
                ? "0 users"
                : `${offset + 1}–${Math.min(offset + PAGE_SIZE, total)} of ${total}`}
            </span>
            <button
              className="access-btn access-btn--ghost access-btn--sm"
              disabled={!hasNext}
              onClick={() => goToPage(page + 1)}
              data-testid="pagination-next"
            >
              NEXT →
            </button>
          </div>
        </>
      )}

      {selectedUserId && (
        <UserDetail userId={selectedUserId} onClose={() => setSelectedUserId(null)} />
      )}
    </div>
  );
}
